import React, { useEffect, useRef, useState } from 'react';
import { Copy, Check, Printer, Plus, Trash2, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import { PageEditProvider, usePageEditMode } from '@/context/PageEditModeContext';
import { PageEditToolbar } from '@/components/edit/PageEditToolbar';
import { PageEditable } from '@/components/edit/PageEditable';

/* ────────────────────────────────────────────────────────────────────────── */
/*  Answer card — inline-editable, with copy button + live character counter */
/* ────────────────────────────────────────────────────────────────────────── */

const AnswerCard = ({ number, tag, question, path, defaultAnswer, limit = 1000, onDelete }) => {
  const { enabled } = usePageEditMode();
  const answerRef = useRef(null);
  const cardRef = useRef(null);
  const [count, setCount] = useState(defaultAnswer.length);
  const [copied, setCopied] = useState(false);

  // Live char count from the actual DOM (works with inline-editable content)
  useEffect(() => {
    const el = answerRef.current;
    if (!el) return;
    const measure = () => setCount((el.innerText || '').length);
    measure();
    const obs = new MutationObserver(measure);
    obs.observe(el, { childList: true, characterData: true, subtree: true });
    return () => obs.disconnect();
  }, []);

  const handleCopy = async () => {
    const text = answerRef.current?.innerText || defaultAnswer;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Answer copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Copy failed — select the text manually');
    }
  };

  const handlePrintOne = () => {
    document.body.setAttribute('data-print-only', cardRef.current?.dataset.cardId || '');
    setTimeout(() => {
      window.print();
      setTimeout(() => document.body.removeAttribute('data-print-only'), 500);
    }, 50);
  };

  const pct = Math.min(100, Math.round((count / limit) * 100));
  const over = count > limit;
  const nearLimit = count > limit * 0.9 && !over;

  return (
    <div ref={cardRef} data-card-id={path} className="grant-card rounded-2xl bg-neutral-900 border border-white/10 overflow-hidden print:bg-white print:border-gray-300 print:break-inside-avoid">
      <div className="flex items-start justify-between gap-4 px-6 pt-5 pb-3 border-b border-white/5 print:border-gray-300">
        <div className="flex items-baseline gap-3 min-w-0">
          <span className="text-yellow-400 font-black text-2xl tabular-nums shrink-0">
            {String(number).padStart(2, '0')}
          </span>
          <p className="text-white/50 text-[10px] font-bold tracking-widest uppercase print:text-gray-600">
            <PageEditable path={`${path}.tag`} as="span" fallback={tag} />
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0 print:hidden">
          {enabled && onDelete && (
            <button
              type="button"
              onClick={onDelete}
              data-edit-safe
              className="p-1.5 rounded-full text-red-400 hover:bg-red-500/10"
              title="Delete this question"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            type="button"
            onClick={handlePrintOne}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-bold transition-colors"
            title="Download this answer as a PDF"
          >
            <FileDown className="w-3.5 h-3.5" /> PDF
          </button>
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-yellow-500 text-black text-xs font-bold hover:bg-yellow-400 transition-colors"
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? 'Copied' : 'Copy answer'}
          </button>
        </div>
      </div>

      <div className="px-6 py-5">
        <PageEditable
          path={`${path}.question`}
          as="h3"
          multiline
          className="text-lg md:text-xl font-bold text-white leading-snug mb-4 print:text-black"
          fallback={question}
        />

        <div ref={answerRef}>
          <PageEditable
            path={`${path}.answer`}
            as="div"
            multiline
            className="text-white/85 leading-relaxed whitespace-pre-line text-base print:text-black"
            fallback={defaultAnswer}
          />
        </div>
      </div>

      {/* Char counter footer */}
      <div className="px-6 py-3 border-t border-white/5 bg-black/30 flex items-center justify-between text-xs print:bg-white print:border-gray-300">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="h-1.5 rounded-full bg-white/10 overflow-hidden flex-1 max-w-xs print:hidden">
            <div
              className={`h-full transition-all ${
                over ? 'bg-red-500' : nearLimit ? 'bg-amber-400' : 'bg-emerald-400'
              }`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <span
            className={`font-mono tabular-nums shrink-0 ${
              over ? 'text-red-400' : nearLimit ? 'text-amber-300' : 'text-white/60'
            } print:text-gray-700`}
          >
            {count.toLocaleString()} / {limit.toLocaleString()}
          </span>
        </div>
        {over && (
          <span className="text-red-400 font-bold uppercase tracking-widest text-[10px] print:hidden">
            Over limit — trim {count - limit} chars
          </span>
        )}
      </div>
    </div>
  );
};

/* ────────────────────────────────────────────────────────────────────────── */
/*  Default questions                                                         */
/* ────────────────────────────────────────────────────────────────────────── */

const DEFAULT_QUESTIONS = [
  {
    tag: 'Novelty · You',
    question: 'Why are you the best person to work on this? What is novel in your approach?',
    defaultAnswer:
      "As Mestre Curtis Pierre, I have spent 38 years — since founding Casa Samba in 1987 — becoming the Afro-Brazilian musical authority in the American South. In 1993 the Mayor of New Orleans proclaimed Casa Samba the city's first samba school. I am both artistic director and physical practitioner: I conduct the ensemble, teach the drummers, coach the dancers, and personally know the masquerade artist whose regalia will appear onstage.\n\nWhat is novel is the convergence itself. No New Orleans production has ever placed a conductor-led chamber orchestra, seven Afro-Brazilian drummers, three trained dancers, and a single African masquerade artist inside one filmed evening. Museums document these traditions separately. Casa Samba stages them speaking to each other — live, in one room, on one continuous timeline, captured with our own broadcast-grade equipment. The grant funds artists and space, not overhead. That is efficiency 38 years of practice produces."
  },
  {
    tag: 'Success · Vision',
    question:
      "What could success look like 1 year from now? What will it look like if you're successful beyond your wildest dreams?",
    defaultAnswer:
      "ONE YEAR:\nAncestors Awaken premieres in New Orleans as a filmed 60–75 minute concert — one conductor, five to six chamber players, seven master drummers, three dancers, one African masquerade. A free public matinee serves schools and low-income families. The film releases with a digital study guide and streaming rights donated back to Casa Samba. A 10–15 minute making-of documentary travels as an outreach tool.\n\nBEYOND WILDEST DREAMS:\nThe premiere becomes an annual New Orleans event. The film enters ethnomusicology curricula, PBS broadcasts, and international festivals. The ensemble tours — Bahia, Lagos, Kingston. Young New Orleanians grow up seeing their African, Brazilian, and Louisiana lineages onstage together, understood as one continuous inheritance. The Casa Samba archive becomes the world's authority on this convergence — not because we said so, but because for one evening, we simply made it audible."
  },
  {
    tag: 'Failure · Honesty',
    question:
      'What might failure look like? What could be some of the original mistakes of your project?',
    defaultAnswer:
      "The clearest failure mode is spectacle without substance — a beautifully filmed evening that impresses reviewers but leaves the artists underpaid, the community uninvited, and the masquerade decontextualized. If Ancestors Awaken becomes a show ABOUT African and Brazilian traditions instead of a show BY the practitioners of those traditions, we have failed regardless of production quality.\n\nA second mistake would be treating this as a one-off event rather than a first cycle. The four-tier structure of this proposal exists precisely because scale should follow trust: a $5,000 pilot proves the concept; a $30,000 full production only makes sense if the community, the artists, and the funder are all still in the room.\n\nA third would be over-producing the film at the cost of the live experience. The audience in New Orleans on the night of the performance must feel that the room, and the ancestors, came first — and the camera came second."
  },
  {
    tag: 'Gender · Equity',
    question:
      'How does your project address gender equity, representation, or the experiences of women, femme, non-binary and gender-diverse people?',
    defaultAnswer:
      "Ancestors Awaken is built on gender-balanced leadership on and off the stage. Of the fifteen artists in the full ensemble, women and femme-identifying artists occupy principal roles in the chamber orchestra, all three dance positions, and vocal leadership — reflecting the matrilineal weight that Afro-Brazilian and West African musical traditions actually carry, where the mães-de-santo (mothers of the terreiro), the porta-bandeiras (flag bearers), and the passistas hold as much ancestral authority as any drummer.\n\nOur pay structure is transparent and flat: dancers and drummers receive equivalent per-rehearsal and performance stipends. Casa Samba has, since 1987, been a rare cultural institution where a young girl in New Orleans can watch a Black woman lead an ensemble. This production continues that lineage and puts it on film — so the visibility scales past the room.\n\nWe welcome and actively include LGBTQ+ and gender-diverse artists as full ensemble members. There is no separate 'women's section' or token role — the work is the work, and the work is shared."
  },
  {
    tag: 'Cultural & Economic Impact',
    question:
      'Short statement of anticipated cultural AND economic impact of the proposed program, including specific measurable anticipated results.',
    defaultAnswer:
      "At the full $30,000 tier, Ancestors Awaken directly employs 15 cultural workers: one conductor, six chamber-orchestra players, seven master drummers led by Mestre Curtis Pierre, three principal dancers, and one African masquerade artist — plus a videographer, live-audio engineer, editor, and composer. That is 19 paid engagements over a 12-month rehearsal-to-premiere cycle, with 61% of the budget flowing directly to artist honoraria and stipends.\n\nThe premiere serves 400–500 ticketed attendees plus a free public matinee for 200 New Orleans children drawn from Title I and after-school programs — 700+ live participants. The resulting 60–75-minute concert film, distributed with a bilingual study guide to public schools, museum education departments, and streaming partners, is projected to reach 10,000+ secondary viewers in year one.\n\nCultural enrichment centers on New Orleans's Afro-Diasporic lineage. Audiences encounter kora, baloan, omuko, and ngoni — ancestral West-African string instruments rarely seen on U.S. concert stages — in living dialogue with a symphony string section and a masquerade tradition, framed by Mestre Curtis Pierre's 38 years of practice and Casa Samba's 1993 mayoral proclamation as New Orleans's first samba school.\n\nUnder-resourced demographics reached: Black K–12 students in Orleans and Jefferson Parishes, Title I schools, elders offered priority free admission, and neighborhood youth programs already inside the Casa Samba network. The grant does not build new capacity — it releases 38 years of accumulated capacity into one filmed evening and multiplies it through free educational distribution."
  },
  {
    tag: 'Sustainability · Leverage',
    question:
      "Short description of the organization's sustainability plans and how awarded funds would be leveraged into future results — how the organization supports itself and how awarded funds bridge a gap to reduce additional financial need.",
    defaultAnswer:
      "Escola de Samba Casa Samba has sustained itself for 38 years — since 1987 — through a diversified earned-income model rather than grant dependence. Current revenue streams include:\n\n• Weekly adult and kids' classes (drop-in through yearly tiers, $10–$250)\n• Private lessons in samba drumming, dance, capoeira, pandeiro, percussion and stilt-walking\n• Performance fees from annual engagements — Zulu Ball, Endymion, New Orleans Jazz & Heritage Festival, hotel and corporate events\n• Digital revenue: on-demand video courses, sample-pack store, membership tiers, pay-per-view events\n• Drum-repair service and traditional-instrument workshops\n• The Kongo of NOLA merchandise and cultural-tourism programs\n\nCasa Samba already owns the multi-camera media-capture equipment and holds the venue relationships this project needs — which is why 61% of the requested budget flows directly to artist honoraria rather than infrastructure.\n\nNOTCF funds bridge a specific, measurable gap: paying the 15+ living artists — conductor, orchestra, drummers, dancers, masquerade — for the twelve months of rehearsal and one filmed premiere that our earned income cannot yet cover at production quality. Every subsequent cycle becomes cheaper: costumes, arrangements, staging, and the film's distribution infrastructure become permanent assets that reduce the ask year over year.\n\nDownstream leverage: the concert film generates streaming revenue back to Casa Samba; the bilingual study guide opens paid school-residency contracts; and the annual Ancestors Awaken event creates a repeatable ticketed anchor around which corporate sponsorship, museum partnerships, and touring engagements can be built."
  }
];

/* ────────────────────────────────────────────────────────────────────────── */
/*  Main page                                                                 */
/* ────────────────────────────────────────────────────────────────────────── */

function GrantWorkspaceInner() {
  const { enabled } = usePageEditMode();
  const [questions, setQuestions] = useState(() =>
    DEFAULT_QUESTIONS.map((q, i) => ({ ...q, id: `q${i + 1}` }))
  );

  const addQuestion = () => {
    setQuestions((qs) => [
      ...qs,
      {
        id: `q${Date.now()}`,
        tag: 'New Question',
        question: 'New grant question — click to edit.',
        defaultAnswer: 'Draft your answer here. Character counter is live below.'
      }
    ]);
  };

  const removeQuestion = (id) => {
    if (!window.confirm('Remove this question card?')) return;
    setQuestions((qs) => qs.filter((q) => q.id !== id));
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-white print:bg-white print:text-black">
      <style>{`
        @media print {
          @page { size: letter portrait; margin: 0.5in; }
          html, body { background: white !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          /* When a specific card is selected for print, hide all others */
          body[data-print-only] .grant-card:not([data-card-id]) { display: none !important; }
          body[data-print-only="q.q1"] .grant-card[data-card-id]:not([data-card-id="q.q1"]),
          body[data-print-only="q.q2"] .grant-card[data-card-id]:not([data-card-id="q.q2"]),
          body[data-print-only="q.q3"] .grant-card[data-card-id]:not([data-card-id="q.q3"]),
          body[data-print-only="q.q4"] .grant-card[data-card-id]:not([data-card-id="q.q4"]),
          body[data-print-only="q.q5"] .grant-card[data-card-id]:not([data-card-id="q.q5"]),
          body[data-print-only="q.q6"] .grant-card[data-card-id]:not([data-card-id="q.q6"]) { display: none !important; }
          body[data-print-only] .no-print-when-single { display: none !important; }
        }
      `}</style>
      {/* Header */}
      <div className="border-b border-white/10 bg-neutral-900/70 backdrop-blur-md sticky top-0 z-40 print:hidden">
        <div className="max-w-4xl mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-yellow-400 text-[10px] font-bold tracking-[0.35em] uppercase mb-0.5">
              Grant Workspace
            </p>
            <h1 className="text-lg md:text-xl font-black leading-tight">
              <PageEditable path="workspace.title" as="span" fallback="NOTCF — Ancestors Awaken" />
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => window.print()}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-bold transition-colors"
            >
              <Printer className="w-3.5 h-3.5" /> Print all
            </button>
          </div>
        </div>
      </div>

      {/* Intro */}
      <div className="max-w-4xl mx-auto px-6 pt-10 pb-4 print:pt-6">
        <PageEditable
          path="workspace.intro"
          as="p"
          multiline
          className="text-white/60 leading-relaxed text-sm md:text-base print:text-gray-700"
          fallback="Scratchpad for the NOTCF grant. Click any answer to edit. Character counter tracks the 1,000-char NOTCF limit live. Click Copy on any card to paste it straight into the portal."
        />
      </div>

      {/* Question cards */}
      <div className="max-w-4xl mx-auto px-6 py-6 space-y-6 print:py-2">
        {questions.map((q, i) => (
          <AnswerCard
            key={q.id}
            number={i + 1}
            tag={q.tag}
            question={q.question}
            defaultAnswer={q.defaultAnswer}
            path={`q.${q.id}`}
            onDelete={enabled ? () => removeQuestion(q.id) : undefined}
          />
        ))}

        {enabled && (
          <button
            type="button"
            onClick={addQuestion}
            data-edit-safe
            data-testid="add-grant-question-btn"
            className="w-full py-6 rounded-2xl border-2 border-dashed border-yellow-400/40 bg-yellow-400/5 hover:bg-yellow-400/10 hover:border-yellow-400 text-yellow-300 font-bold flex flex-col items-center gap-1 transition-colors print:hidden"
          >
            <Plus className="w-6 h-6" />
            Add another grant question
          </button>
        )}
      </div>

      {/* Footer */}
      <div className="max-w-4xl mx-auto px-6 pb-16 pt-2 print:pb-4">
        <p className="text-center text-[10px] text-white/40 tracking-widest uppercase print:text-gray-500">
          Casa Samba · Escola de Samba · Grant Workspace · Not public
        </p>
      </div>
    </div>
  );
}

const GrantWorkspace = () => (
  <PageEditProvider slug="grant-workspace">
    <GrantWorkspaceInner />
    <PageEditToolbar />
  </PageEditProvider>
);

export { GrantWorkspace };
export default GrantWorkspace;
